import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useQuery } from '@tanstack/react-query';
import { API_ROUTES } from '../constants/apiRoutes';
import { useMarketplaceConfig } from './marketplace';
import { getData } from '../utils/api';
import { withdrawalCurrenciesSchema, type ZeroXWithdrawalCurrency } from '../schemas/zeroXWithdrawal';
import { depositCurrenciesSchema, depositMinimumSchema } from '../schemas/zeroXDeposit';

export const useZeroXDepositCurrencies = () => {
  const { userId, isAuthenticated } = useAuth();
  const { data: marketplace } = useMarketplaceConfig();

  return useQuery<{ currencies: string[] }>({
    queryKey: ['zero-x-deposit-currencies', marketplace?.id, userId],
    enabled: isAuthenticated && Boolean(userId),
    queryFn: async () => {
      const response = await getData(API_ROUTES.ZERO_X.CURRENCIES, {}, 'wallet');

      return depositCurrenciesSchema.parse(response);
    },
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useZeroXWithdrawalCurrencies = () => {
  const { userId, isAuthenticated } = useAuth();

  // The wallet derives the marketplace from the token; only the player identity can change at runtime.
  return useQuery<ZeroXWithdrawalCurrency[]>({
    queryKey: ['zero-x-withdrawal-currencies', userId],
    enabled: isAuthenticated && Boolean(userId),
    queryFn: async () => {
      const response = await getData(API_ROUTES.ZERO_X.WITHDRAWAL_CURRENCIES, {}, 'wallet');

      return withdrawalCurrenciesSchema.parse(response).currencies;
    },
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useZeroXDepositMinimum = (currency: string) => {
  const { userId, isAuthenticated } = useAuth();
  const { data: marketplace } = useMarketplaceConfig();

  return useQuery({
    queryKey: ['zero-x-deposit-minimum', marketplace?.id, userId, currency],
    enabled: isAuthenticated && Boolean(userId) && Boolean(currency),
    queryFn: async () => {
      const minimum = depositMinimumSchema.parse(
        await getData(API_ROUTES.ZERO_X.DEPOSIT_MINIMUM, { currency }, 'wallet')
      );

      if (minimum.currency !== currency) {
        throw new Error('Deposit minimum currency does not match selection');
      }

      return minimum;
    },
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
};
